import { CanvasObject } from "./object.js";

import { BezierCurve } from "../prototypes/math/bezier.js";

class VectorObject extends CanvasObject {
    constructor({
        context,
        saveState = null
    }){
        super(context);
        this.name = "Vector Object";

        this.vertices = (saveState ? saveState.vertices : []);
        this.curves = [];

        this.fill = (saveState && saveState.fill ? saveState.fill : {
            enabled: false,
            color: '#000000'
        });
        this.stroke = (saveState && saveState.stroke ? saveState.stroke : {
            enabled: true,
            color: '#FFFFFF',
            width: 1
        });
        this.effects = {
            enabled: false,
            shadow: 'none'
        }

        this.resolution = 6;
        this.debug = false;
        
        this._init();
    }
    _init() {
        this._getObjectBoundingRect();
        this.preview = null;
        this._onPenMove = (event) => {
            if (!this.editing || this.vertices.length === 0) return;
            this.preview = {
                x: (event.detail.controls.mouse.x - event.detail.canvasOffset.x + (event.detail.position.x * -1)) / event.detail.zoom,
                y: (event.detail.controls.mouse.y - event.detail.canvasOffset.y + (event.detail.position.y * -1)) / event.detail.zoom
            };
        };
        this.context.canvas.addEventListener('creator:pen:move', this._onPenMove);
    }
    _cursorVertex() {
        const ctx = this.context;
        const radius = 10;

        ctx.fillStyle = 'grey';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;

        ctx.beginPath();
        for (let i = 0; i < this.vertices.length; i++) {
            const v = this.vertices[i];
            const r = radius / 2; // match old square size visually
            // Avoid connecting circles by moving to circle edge before drawing arc
            ctx.moveTo(v.x + r, v.y);
            ctx.arc(v.x, v.y, r, 0, Math.PI * 2);
        }
        ctx.closePath();
        ctx.fill();
    }
    _editModeRender() {
        if( !this.editing ) return;

        const ctx = this.context;

        if (this.preview && this.vertices.length > 0) {
            // there must be another function that adds a vertex then we show the preview, 
            // otherwise we don't show it
            ctx.fillStyle = 'red';
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 2;
            ctx.beginPath();
            const last = this.vertices[this.vertices.length - 1];
            ctx.moveTo(last.x, last.y);
            ctx.lineTo(this.preview.x, this.preview.y);
            ctx.stroke();
        }
        // you want to render the control points
        this.vertices.filter( v => v.control_points ).forEach( v => {
            v.control_points.forEach( cp => {
                ctx.fillStyle = 'white';
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.arc(cp.x, cp.y, 5, 0, Math.PI * 2);
                ctx.fill();
                ctx.fill();
                ctx.stroke();

                // this is the dashed line
                ctx.beginPath();
                ctx.moveTo(cp.x, cp.y);
                ctx.setLineDash([8, 4]);
                ctx.lineTo(v.x, v.y);
                ctx.stroke();

            });
        });

        this._cursorVertex();
    }
    _getObjectBoundingRect() {
        if (!this.vertices || this.vertices.length === 0) {
            this.boundingRect = { x: 0, y: 0, width: 0, height: 0 };
            return this.boundingRect;
        }

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const v of this.vertices) {
            if (v.x < minX) minX = v.x;
            if (v.y < minY) minY = v.y;
            if (v.x > maxX) maxX = v.x;
            if (v.y > maxY) maxY = v.y;
        }

        // Expand to include actual curve extents (not control handles)
        if (this.curves && this.curves.length) {
            this.curves.forEach(curve => {
                if (!curve.parameter) return;
                const { min, max } = curve.parameter;
                if (Number.isFinite(min?.x) && min.x < minX) minX = min.x;
                if (Number.isFinite(min?.y) && min.y < minY) minY = min.y;
                if (Number.isFinite(max?.x) && max.x > maxX) maxX = max.x;
                if (Number.isFinite(max?.y) && max.y > maxY) maxY = max.y;
            });
        }

        // Inflate bounds by half the stroke width so the full painted path fits
        const halfStroke = (this.stroke && this.stroke.enabled ? this.stroke.width : 0) / 2;
        if (halfStroke > 0 && Number.isFinite(minX)) {
            minX -= halfStroke;
            minY -= halfStroke;
            maxX += halfStroke;
            maxY += halfStroke;
        }

        const rect = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
        this.boundingRect = rect;
        this.position.x = this.boundingRect.x;
        this.position.y = this.boundingRect.y;
        this.dimensions.width = this.boundingRect.width;
        this.dimensions.height = this.boundingRect.height;
        return rect;
    }
    _getNeighBourVertices({
        vertex_index
    }) {
        const left_vertex_index = vertex_index - 1;
        const right_vertex_index = vertex_index + 1;

        let neighbour_vertices = [];

        if( left_vertex_index >= 0 ) neighbour_vertices.push(left_vertex_index);
        if( right_vertex_index < this.vertices.length ) neighbour_vertices.push(right_vertex_index);
        
        return neighbour_vertices;
    }
    _createControlPoints({
        vertex_index,
        offset = 100
    }) {
        if( this.vertices[vertex_index].control_points ) return;

        const neighbour_vertices_indexes = this._getNeighBourVertices({vertex_index});
        const vertex = this.vertices[vertex_index];

        // Need at least one neighbour to define a direction
        if (!neighbour_vertices_indexes.length) return;

        let dx = 0;
        let dy = 0;

        if (neighbour_vertices_indexes.length === 2) {
            // Direction from previous to next vertex
            const prev = this.vertices[neighbour_vertices_indexes[0]];
            const next = this.vertices[neighbour_vertices_indexes[1]];
            dx = next.x - prev.x;
            dy = next.y - prev.y;
        } else {
            const neighbourIndex = neighbour_vertices_indexes[0];
            const neighbour = this.vertices[neighbourIndex];

            // Direction along the path (in index order)
            if (neighbourIndex < vertex_index) {
                // Path goes neighbour -> vertex
                dx = vertex.x - neighbour.x;
                dy = vertex.y - neighbour.y;
            } else {
                // Path goes vertex -> neighbour
                dx = neighbour.x - vertex.x;
                dy = neighbour.y - vertex.y;
            }
        }

        let length = Math.hypot(dx, dy);
        if (!length) {
            // Degenerate case: fall back to horizontal tangent
            dx = 1;
            dy = 0;
            length = 1;
        }

        const ux = dx / length;
        const uy = dy / length;

        const gradient = dx === 0 ? null : (dy / dx);

        this.vertices[vertex_index].control_points = [
            // Handle pointing "backwards" along the path
            {
                x: vertex.x - ux * offset,
                y: vertex.y - uy * offset,
                gradient,
                vertex_index
            },
            // Handle pointing "forwards" along the path
            {
                x: vertex.x + ux * offset,
                y: vertex.y + uy * offset,
                gradient,
                vertex_index
            }
        ];
    }
    _getVertexIndex({
        dragging = true
    }) {
        let vertex_index = null;

        this.vertices.forEach( (v, i) => {
            if( v.dragging === dragging ) vertex_index = i;
        });

        return vertex_index;
    }
    isCursorOnObject({x, y} = {}) {
        // Convert screen coords to canvas local coords (accounting for pan/offset)
        const localX = x;
        const localY = y;

        // Fallback: if no vertices, nothing to hit
        if (!this.vertices || this.vertices.length === 0) return false;

        // Quick reject via bounding box (with some tolerance)
        const tolerance = 6 * (window.devicePixelRatio || 1);
        const { x: rx, y: ry, width, height } = this._getObjectBoundingRect();
        if (
            localX < rx - tolerance ||
            localY < ry - tolerance ||
            localX > rx + width + tolerance ||
            localY > ry + height + tolerance
        ) return false;

        // If filled, detect inside polygon (point-in-polygon)
        if (this.fill.enabled && this.vertices.length >= 3) {
            let inside = false;
            for (let i = 0, j = this.vertices.length - 1; i < this.vertices.length; j = i++) {
                const xi = this.vertices[i].x, yi = this.vertices[i].y;
                const xj = this.vertices[j].x, yj = this.vertices[j].y;
                const intersect = ((yi > localY) !== (yj > localY)) &&
                    (localX < (xj - xi) * (localY - yi) / ((yj - yi) || 1e-12) + xi);
                if (intersect) inside = !inside;
            }
            if (inside) return true;
            // fallthrough to edge/vertex test for clicks right on border
        }

        // Hit test: near any segment in the polyline
        const distToSegment = (px, py, x1, y1, x2, y2) => {
            const dx = x2 - x1;
            const dy = y2 - y1;
            const len2 = dx*dx + dy*dy;
            if (len2 === 0) return Math.hypot(px - x1, py - y1);
            let t = ((px - x1) * dx + (py - y1) * dy) / len2;
            t = Math.max(0, Math.min(1, t));
            const projX = x1 + t * dx;
            const projY = y1 + t * dy;
            return Math.hypot(px - projX, py - projY);
        };

        for (let i = 1; i < this.vertices.length; i++) {
            const v1 = this.vertices[i - 1];
            const v2 = this.vertices[i];
            if (distToSegment(localX, localY, v1.x, v1.y, v2.x, v2.y) <= tolerance) return true;
        }

        // Hit test: near any Bezier curve making up this vector
        if (this.curves && this.curves.length) {
            for (const curve of this.curves) {
                if (curve.isOnBezierCurve && curve.isOnBezierCurve({
                    x: localX,
                    y: localY,
                    tolerance
                })) {
                    return true;
                }
            }
        }

        // Also allow clicking directly on a vertex square
        const vertexRadius = 10; // matches _cursorVertex
        for (const v of this.vertices) {
            if (
                localX >= v.x - vertexRadius / 2 &&
                localX <= v.x + vertexRadius / 2 &&
                localY >= v.y - vertexRadius / 2 &&
                localY <= v.y + vertexRadius / 2
            ) return true;
        }

        return false;
    }
    isCursorOnVertex({x, y} = {}) {
        let vertex_index = null;

        this.vertices.forEach( (v, i) => {
            const radius = 10;
            if (
                x < v.x + radius && 
                x > v.x - radius && 
                y < v.y + radius && 
                y > v.y - radius
            ) {
                vertex_index = i;
            }
        });

        return vertex_index
    }
    isCursorOnControlPoint({x, y} = {}) {
        // we have to find all the control points of all vertices
        const all_control_points = [];

        this.vertices.filter( v => v.control_points ).forEach( v => {
            v.control_points.forEach( (cp, i) => {
                cp.index = i;
                all_control_points.push(cp);
            });
        });

        // looks like we going to have to add the gradient to the control point
        if( !all_control_points.length ) return false;

        // let control_point = null;
        let radius = 10;

        const [control_point] = all_control_points.filter( cp => {
            return x < cp.x + radius && x > cp.x - radius && y < cp.y + radius && y > cp.y - radius
        });
    
        return control_point;
    }
    startDragControlPoint({x, y, control_point}) {
        const vertex = this.vertices[control_point.vertex_index];

        if( !vertex ) return;

        let _control_point = vertex.control_points[control_point.index];

        if( !_control_point ) return;

        vertex.control_points[control_point.index] = {
            ...vertex.control_points[control_point.index],
            before: {
                x: x,
                y: y
            },
            selected: true
        }
    }
    startDragObject({controls} = {}) {
        this.dragging = true;

        this.vertices.filter( v => v.control_points ).forEach( v => {
            v.control_points.forEach( cp => {
                cp.before = { x: cp.x, y: cp.y };
            });
        });

        this.vertices = this.vertices.map( v => {
            return {
                ...v,
                before: {
                    x: v.x,
                    y: v.y
                }
            }
        });

        controls.mouse.before = {
            x: controls.mouse.x,
            y: controls.mouse.y
        }
    }
    startResizeObject({controls, resize_boundary} = {}) {
        this.resizing = true;
        this.resize_boundary = resize_boundary;

        this.vertices.filter(v => v.control_points).forEach(v => {
            v.control_points.forEach(cp => {
                cp.before = { x: cp.x, y: cp.y };
            });
        });

        this.vertices = this.vertices.map( v => {
            return {
                ...v,
                before: {
                    x: v.x,
                    y: v.y
                }
            }
        });

        this.dimensions.before = {
            width: this.dimensions.width,
            height: this.dimensions.height
        }

        this.position.before = {
            x: this.position.x,
            y: this.position.y
        }

        controls.mouse.before = {
            x: controls.mouse.x,
            y: controls.mouse.y
        }
    }
    startDragVertex({
        x,
        y, 
        vertex_index
    } = {}) {
        this.vertices[vertex_index].dragging = true;

        if( this.vertices[vertex_index].control_points ) {
            this.vertices[vertex_index].control_points.forEach( cp => {
                cp.before = { x: cp.x, y: cp.y };
            });
        }

        this.vertices = this.vertices.map(v => {
            return {
                ...v,
                before: {
                    x: v.x,
                    y: v.y
                }
            }
        });
    }
    dragControlPoint({x, y}) {
        const [vertex] = this.vertices.filter( v => {
            return v.control_points && v.control_points.some( cp => cp.selected );
        });

        if( !vertex ) return;

        const [control_point] = vertex.control_points.filter( (cp, i) => {
            return cp.selected;
        });

        // console.log({x, y})
        // console.log(control_point)
        control_point.x = control_point.before.x + x;
        control_point.y = control_point.before.y + y;
    }
    dragVertex({x, y, vertex_index}) {
        let vertex = this.vertices[vertex_index];

        if( !vertex ) return;

        this.vertices[vertex_index].x = this.vertices[vertex_index].before.x + x;
        this.vertices[vertex_index].y = this.vertices[vertex_index].before.y + y;

        if( !this.vertices[vertex_index].control_points ) return;

        this.vertices[vertex_index].control_points.forEach( cp => {
            cp.x = cp.before.x + x;
            cp.y = cp.before.y + y;
        });
    }
    stopVertexDrag() {
        this.preview = null;
        this.vertices.forEach(v => {
            v.dragging = false
        });
    }
    stopControlPointDrag() {
        this.vertices.filter( v => v.control_points ).forEach( v => {
            v.control_points.forEach( cp => cp.selected = false );
        });
    }
    drawPath({
        context=null
    } = {}) {
        const ctx = (context) ? context : this.context;

        this.curves = [];

        ctx.fillStyle = this.fill.color;
        ctx.lineWidth = this.stroke.width;
        ctx.strokeStyle = this.stroke.color;

        // before we render anything we first need to process the path.
        let previous_vertex = null;
        let next_vertex = null;

        this.vertices.forEach( (v, i) => {
            if( i+1 > this.vertices.length+1 ) {
                next_vertex = null;
            } else {
                next_vertex = this.vertices[i+1];
            }

            if( i === 0 ) {
                previous_vertex = v;
                return;
            }
            // look for control points
            // and store the vertex
            if( v.curve && v.curve === true ) {
                previous_vertex.curve = true;
                // console.log(next_vertex)
                if(next_vertex) next_vertex.curve = true;
            };

            previous_vertex = v;
        });

        // actual render.
        ctx.beginPath();
        this.vertices.forEach((v, i) => {
            if (i === 0) {
                ctx.moveTo(v.x, v.y);
                previous_vertex = v;
                return;
            }

            let drewCurve = false;

            if (v.curve) {
                const v1 = previous_vertex;
                let cp1 = null;
                let cp2 = null;

                if (previous_vertex.control_points && previous_vertex.control_points.length > 1) {
                    cp1 = previous_vertex.control_points[1];
                }
                if (v.control_points && v.control_points.length) {
                    cp2 = v.control_points[0];
                }

                const bezierCurve = new BezierCurve({
                    context: ctx,
                    resolution: this.resolution,
                    debug: this.debug
                });

                if (cp1 && cp2) {
                    // Cubic: both vertices contribute handles
                    bezierCurve.draw({
                        A: v1,
                        B: cp1,
                        C: cp2,
                        D: v
                    });
                    this.curves.push(bezierCurve);
                    drewCurve = true;
                } else if (cp1 || cp2) {
                    // Quadratic: only one handle available
                    const cp = cp1 || cp2;
                    bezierCurve.draw({
                        A: v1,
                        B: cp,
                        C: v
                    });
                    this.curves.push(bezierCurve);
                    drewCurve = true;
                }
            }

            // Fallback / non-curve: ensure we still render a straight segment
            if (!drewCurve) {
                ctx.lineTo(v.x, v.y);
            }

            previous_vertex = v;
        });
        ctx.stroke();

        // console.log(this.vertices);
        // ctx.stroke();
        // ctx.closePath();
    }
    render() {
        this.drawPath();
        
        if(this.fill.enabled) this.context.closePath();
        if(this.stroke.enabled) this.context.stroke();
        if(this.fill.enabled) this.context.fill();
        if(this.selected && !this.editing) this.showSelected();
        if( this.editing ) this._editModeRender();
    }
    getState() {
        return {
            type: "VectorObject",
            vertices: [ ...this.vertices ],
            position: { ...this.position },
            fill: { ...this.fill },
            stroke: { ...this.stroke },
        }
    }
    addVertex({x, y}) { this.vertices.push({x, y}); }
    moveTo({x, y, before}) {
        this.position = {x, y, before};

        this.vertices.forEach( v => {
            v.x = v.before.x + x;
            v.y = v.before.y + y;
        });
        
        this.vertices.filter( v => v.control_points ).forEach( v => {
            v.control_points.forEach( cp => {
                cp.x = cp.before.x + x;
                cp.y = cp.before.y + y;
            });
        });
        this._getObjectBoundingRect();
    }
    resize({position, dimensions, cursor}) {
        // console.log({width, height});
        // console.log(before);
        // before.width

        const leftEdge = this.resize_boundary.type === 'edge' && this.resize_boundary.name === 'left';
        const rightEdge = this.resize_boundary.type === 'edge' && this.resize_boundary.name === 'right';
        const topEdge = this.resize_boundary.type === 'edge' && this.resize_boundary.name === 'top';
        const bottomEdge = this.resize_boundary.type === 'edge' && this.resize_boundary.name === 'bottom';

        const bottomLeftCorner = this.resize_boundary.type === 'bottom.corner' && this.resize_boundary.name === 'left';
        const bottomRightCorner = this.resize_boundary.type === 'bottom.corner' && this.resize_boundary.name === 'right';
        const topLeftCorner = this.resize_boundary.type === 'top.corner' && this.resize_boundary.name === 'left';
        const topRightCorner = this.resize_boundary.type === 'top.corner' && this.resize_boundary.name === 'right';

        const computeOffset = (beforePoint) => {
            let bx = Math.abs((beforePoint.x - position.before.x)) / dimensions.before.width;
            let by = Math.abs((beforePoint.y - position.before.y)) / dimensions.before.height;

            let width_ratio = (dimensions.width / dimensions.before.width);
            let height_ratio = (dimensions.height / dimensions.before.height);

            let ratio_x = bx * (width_ratio - 1);
            let ratio_y = by * (height_ratio - 1);

            let c_w = dimensions.before.width * ratio_x;
            let c_y = dimensions.before.height * ratio_y;

            if( leftEdge ) {
                c_w = cursor.x + dimensions.before.width * -ratio_x;
                c_y = 0;
            } else if( topEdge ) {
                c_w = 0;
                c_y = cursor.y + dimensions.before.height * -ratio_y;
            } else if ( rightEdge ) {
                c_w = dimensions.before.width * ratio_x;
                c_y = 0;
            } else if( bottomEdge ) {
                c_w = 0;
                c_y = dimensions.before.height * ratio_y;
            } else if( bottomLeftCorner ) {
                c_w = cursor.x + dimensions.before.width * -ratio_x;
                c_y = dimensions.before.height * ratio_y;
            } else if( topLeftCorner ) {
                c_w = cursor.x + dimensions.before.width * -ratio_x;
                c_y = cursor.y + dimensions.before.height * -ratio_y;
            } else if( topRightCorner ) {
                c_w = dimensions.before.width * ratio_x;
                c_y = cursor.y + dimensions.before.height * -ratio_y;
            }

            return { c_w, c_y };
        };

        this.vertices.forEach(v => {
            const { c_w: v_w, c_y: v_y } = computeOffset(v.before);

            if( v.control_points ) {
                v.control_points.forEach(cp => {
                    const { c_w: cp_w, c_y: cp_y } = computeOffset(cp.before || cp);
                    cp.x = cp.before.x + cp_w;
                    cp.y = cp.before.y + cp_y;
                });
            }

            v.x = v.before.x + v_w;
            v.y = v.before.y + v_y;
        })

        this._getObjectBoundingRect();
    }
}

export {
    VectorObject
}

